import { apiClient, getPlatformUrl } from "../../../../lib/api";
import store from "../_store";
import { applySubgroupingConditions } from "../../_lib/subgrouping";
import {
  setStratificationSubgroup,
  setStratificationFuncOn,
} from "../_store/causal";

export const getModels = async ({ analysis_id, page }) => {
  const url = `${getPlatformUrl()}/causal-inference/analysis/${analysis_id}/dwmodel?page=${page}&items_per_page=5&is_fixed=true`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const getAnalyses = async ({ page }) => {
  const url = `${getPlatformUrl()}/causal-inference/analysis?page=${page}&items_per_page=5`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const makeAnalysis = async (req) => {
  const body = { name: req };
  const url = `${getPlatformUrl()}/causal-inference/analysis`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};

export const fetchAllAnalysis = async () => {
  const url = `${getPlatformUrl()}/causal-inference/analysis?page=1&items_per_page=1000`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res.analyses;
};

export const getModelsFromAnalysis = async (analysisID) => {
  const url = `${getPlatformUrl()}/causal-inference/analysis/${analysisID}/dwmodel?page=1&items_per_page=1000&is_fixed=true`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const makeModel = async (body) => {
  const url = `${getPlatformUrl()}/causal-inference/bnmodel`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};

export const updateBNModel = async (model_id, name, saveAsCausalAsset = false) => {
  const body = { 
    name,
    is_core_model: saveAsCausalAsset
  };
  const url = `${getPlatformUrl()}/causal-inference/bnmodel/${model_id}`;
  const res = await apiClient.update({ url: url, body: body, useToken: true });
  return res;
};

export const deleteBNModel = async (model_id) => {
  const url = `${getPlatformUrl()}/causal-inference/bnmodel/${model_id}`;
  const res = await apiClient.remove({ url: url, useToken: true });
  return res;
};

export const addNode = async (model_id, nodes) => {
  const body = { nodes };
  const url = `${getPlatformUrl()}/causal-inference/bnmodel/${model_id}/node`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};

export const deleteNode = async (model_id, nodes) => {
  const body = { nodes };
  const url = `${getPlatformUrl()}/causal-inference/bnmodel/${model_id}/node`;
  const res = await apiClient.remove({ url: url, body: body, useToken: true });
  return res;
};

export const edgeAvailability = async ({
  model_id,
  add_edges = [],
  reverse_edges = [],
  tentative_edges = null
}) => {
  const body = { add_edges, reverse_edges, tentative_edges };
  const url = `${getPlatformUrl()}/causal-inference/bnmodel/${model_id}/edge-availability`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};

export const addEdge = async (model_id, edges) => {
  const body = { edges };
  const url = `${getPlatformUrl()}/causal-inference/bnmodel/${model_id}/edge`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};

export const deleteEdge = async (model_id, edges) => {
  const body = { edges };
  const url = `${getPlatformUrl()}/causal-inference/bnmodel/${model_id}/edge`;
  const res = await apiClient.remove({ url: url, body: body, useToken: true });
  return res;
};

export const searchStructure = async (model_id, algoType = "hillclimb") => {
  const url = `${getPlatformUrl()}/api/data-robustness/bnmodel/${model_id}/limited-structure-search`;
  const res = await apiClient.post({
    url: url,
    body: {
      algorithm_type: algoType,
      start_dag_edges: [],
      fixed_edges: [],
      black_list: [],
      white_list: [],
      max_indegree: 3,
      structure_search_config: {
        algorithm_type: algoType,
        score_type: "bdeuscore",
        equivalent_sample_size: 30,
      },
    },
    useToken: true,
  });
  return res;
};

const pollMessageQueue = async (task_id) => {
  return await new Promise((resolve) => {
    let intervalId = setInterval(async () => {
      let statusResult = await getStatus(task_id);
      if (statusResult !== undefined && statusResult.status) {
        clearInterval(intervalId);
        let finalResult = await getResult(task_id);
        return resolve(finalResult);
      }
    }, 10000);
  });
};

export const estimateParameter = async (
  model_id,
  equivalent_sample_size = 30,
  estimate_refute = false,
  useQueue = false,
  retry = true
) => {
  let shouldUseQueue = useQueue;

  let url = `${getPlatformUrl()}/api/data-robustness/dwmodel/${model_id}/limited-parameter-estimation`;
  if (shouldUseQueue) {
    url = `${getPlatformUrl()}/api/message-queue/data-robustness/${model_id}/limited-parameter-estimation`;
  }
  const body = {
    estimate_effect_method_type: "backdoor.generalized_linear_model",
    prior_type: "BDeu",
    equivalent_sample_size: equivalent_sample_size,
    estimate_refute: estimate_refute,
  };

  try {
    const res = await apiClient.post({ url: url, body: body, useToken: true });

    if (shouldUseQueue && res && res.task_id) {
      return await pollMessageQueue(res.task_id);
    } else {
      return res;
    }
  } catch (error) {
    if (retry) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      estimateParameter(
        model_id,
        equivalent_sample_size,
        estimate_refute,
        useQueue,
        false
      );
    }
  }
};

export const getMedian = async (model_id, node_id) => {
  const url = `${getPlatformUrl()}/causal-inference/bnmodel/${model_id}/node/${node_id}/distribution`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const loadGraphParameters = async (model_id) => {
  const base = getPlatformUrl();
  //const base = 'http://localhost:8081';
  const url = `${base}/xcausal/v2/graph/parameters/${model_id}`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const saveGraphParameters = async (model_id, data) => {
  const base = getPlatformUrl();
  //const base = 'http://localhost:8081';
  const url = `${base}/xcausal/v2/graph/parameters/${model_id}`;
  const res = await apiClient.update({ url: url, body: { data }, useToken: true });
  return res;
};

export const fixBNModel = async (model_id) => {
  const url = `${getPlatformUrl()}/causal-inference/bnmodel/${model_id}/fix`;
  const res = await apiClient.post({ url: url, useToken: true });
  return res;
};

export const copyWithReports = async (model_id, analysis_id) => {
  const url = `${getPlatformUrl()}/causal-inference/dwmodel/${model_id}/copy-with-reports?analysis_id=${analysis_id}`;
  const res = await apiClient.post({ url: url, useToken: true });
  return res;
};

export const getBNModel = async (newModelId) => {
  const url = `${getPlatformUrl()}/causal-inference/dwmodel/${newModelId}`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const getDoReport = async ({ model_id, page }) => {
  const url = `${getPlatformUrl()}/causal-inference/dwmodel/${model_id}/do-dw?page=${page}&items_per_page=3&is_fixed=true`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const getReport = async (model_id) => {
  const url = `${getPlatformUrl()}/causal-inference/dwmodel/${model_id}/dwmodel-report`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const checkMediator = async ({ model_id, schema }) => {
  const body = schema;
  const url = `${getPlatformUrl()}/causal-inference/dwmodel/${model_id}/check-mediator`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};

export const getStatus = async (task_id) => {
  const url = `${getPlatformUrl()}/api/message-queue/status?task_id=${task_id}`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const getResult = async (task_id) => {
  const url = `${getPlatformUrl()}/api/message-queue/result?task_id=${task_id}`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const renameReport = async (bnmodel_report_id, name) => {
  const body = { name };
  const url = `${getPlatformUrl()}/causal-inference/dwmodel-report/${bnmodel_report_id}`;
  const res = await apiClient.update({ url: url, body: body, useToken: true });
  return res;
};

export const computeDo = async (model_id, schema, retry = true) => {
  const body = schema;
  const url = `${getPlatformUrl()}/api/data-robustness/dwmodel/${model_id}/do-dw`;
  try {
    const res = await apiClient.post({ url: url, body: body, useToken: true });
    return res;
  } catch (error) {
    if (retry) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      computeDo(model_id, schema, false);
    }
  }
};

export const fixModelReport = async (reportId) => {
  const url = `${getPlatformUrl()}/causal-inference/dwmodel-report/${reportId}/fix`;
  const res = await apiClient.post({ url: url, useToken: true });
  return res;
};

export const deleteReport = async (reportId) => {
  const url = `${getPlatformUrl()}/causal-inference/dwmodel-report/${reportId}`;
  const res = await apiClient.remove({ url: url, useToken: true });
  return res;
};

export const reportPublish = async ({ reportId }) => {
  const url = `${getPlatformUrl()}/causal-inference/dwmodel-report-do/${reportId}/publish`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const reportPublishCompare = async ({ reportId1, reportId2 }) => {
  const url = `${getPlatformUrl()}/causal-inference/dwmodel-report-do/${reportId1}/publish-with/${reportId2}`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const getDataset = async ({ bnmodel }) => {
  const url = `${getPlatformUrl()}/causal-inference/dataset/${bnmodel.dataset_metadata_id}`;
  const res = await apiClient.get({ url: url, useToken: true });

  if (res && res.subgrouping_conditions) {
    store.dispatch(setStratificationFuncOn(true));
    if (res.subgrouping_conditions.length > 2) {
      store.dispatch(setStratificationSubgroup(res.subgrouping_conditions[0]));
    } else {
      store.dispatch(setStratificationSubgroup(null));
    }
  } else {
    store.dispatch(setStratificationFuncOn(false));
  }
  return res;
};

export const makeDataset = async (body) => {
  const url = `${getPlatformUrl()}/causal-inference/dataset`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};

export const getJournalSearch = async ({ var1, var2, id, lang, page = 1 }) => {
  const url = `${getPlatformUrl()}/causal-inference/journal_search?var1=${var1}&var2=${var2}&lang=${lang}&report_id=${id}&page=${page}`;
  const res = await apiClient.get({ url: url, useToken: true });
  return res;
};

export const stratificationDo = async (model_id, schema) => {
  // 新 DB は age/gender 固定ではなく取込時の層別化属性 (subject_attrs) から組む。
  const queryContext = store.getState().query?.queryContext;
  const body = {
    ...schema,
    subgrouping_conditions: applySubgroupingConditions(
      schema.subgrouping_conditions,
      queryContext
    ),
  };
  const url = `${getPlatformUrl()}/api/data-robustness/dwmodel/${model_id}/subgrouping-do-dw`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};

export const computeMediator = async ({ model_id, schema }) => {
  const body = schema;
  const url = `${getPlatformUrl()}/api/data-robustness/dwmodel/${model_id}/mediator`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};

export const limitedStructureSearch = async (
  modelID,
  algoType = "hillclimb",
  scoreType = "bdeuscore",
  significanceLevel = "0.05"
) => {
  let body = {
    algorithm_type: algoType,
    start_dag_edges: [],
    fixed_edges: [],
    black_list: [],
    white_list: [],
    max_indegree: 3,
    structure_search_config: {
      algorithm_type: algoType,
      score_type: scoreType,
      equivalent_sample_size: 30,
    },
  };

  if (algoType === "ges") {
    body = {
      algorithm_type: algoType,
      start_dag_edges: [],
      fixed_edges: [],
      black_list: [],
      white_list: [],
      max_indegree: 3,
      structure_search_config: {
        algorithm_type: algoType,
        score_type: scoreType,
        equivalent_sample_size: 30,
      },
    };
  } else if (algoType === "pc") {
    body = {
      algorithm_type: algoType,
      start_dag_edges: [],
      fixed_edges: [],
      black_list: [],
      white_list: [],
      max_indegree: 3,
      structure_search_config: {
        algorithm_type: algoType,
        ci_test: "chi_square",
        max_cond_vars: 4,
        significance_level: parseFloat(significanceLevel),
      },
    };
  } else if (algoType === "mmhc") {
    body = {
      algorithm_type: algoType,
      start_dag_edges: [],
      fixed_edges: [],
      black_list: [],
      white_list: [],
      max_indegree: 3,
      structure_search_config: {
        algorithm_type: algoType,
        score_type: scoreType,
        equivalent_sample_size: 30,
        ci_test: "chi_square",
        max_cond_vars: 4,
        significance_level: parseFloat(significanceLevel),
      },
    };
  }

  const url = `${getPlatformUrl()}/api/data-robustness/bnmodel/${modelID}/limited-structure-search`;
  const res = await apiClient.post({ url: url, body: body, useToken: true });
  return res;
};
